<script setup lang="ts">
import { computed, ref } from 'vue'

import ChevronIcon from '../../assets/icons/chevron.svg?component'
import { formatDateOnly } from '../../formatters'
import { getDateRangeLabel, isDateRangeWithinLimit, MAX_DATE_RANGE_DAYS, shortcutsDatePickerOptions } from '../../utils'
import ButtonComponent from '../atoms/ButtonComponent.vue'
import DatepickerComponent from '../atoms/DatepickerComponent.vue'
import FilterButtonComponent from '../atoms/FilterButtonComponent.vue'
import FlexComponent from '../atoms/FlexComponent.vue'
import TypographyComponent from '../atoms/TypographyComponent.vue'

type Placement = 'bottom' | 'bottom-start' | 'bottom-end'

const props = withDefaults(
  defineProps<{
    hasCustomButton?: boolean
    showClearButton?: boolean
    borderless?: boolean
    placement?: Placement
    maxDays?: number
  }>(),
  {
    maxDays: MAX_DATE_RANGE_DAYS,
    hasCustomButton: false,
    showClearButton: false,
    borderless: false,
    placement: 'bottom',
  },
)
defineSlots<{ customButton(): void }>()

const datePickerRef = ref()
const shortcuts = shortcutsDatePickerOptions()
const modelValue = defineModel<{ startDate: string; endDate: string } | undefined>('modelValue')
const dateRange = computed(() => [modelValue.value?.startDate, modelValue.value?.endDate] as [string, string])
const label = computed(() => getDateRangeLabel(modelValue.value?.startDate, modelValue.value?.endDate))
const popperOptions = computed(() => ({
  placement: props.placement,
  modifiers: [
    {
      name: 'flip',
      options: { fallbackPlacements: [props.placement.replace('bottom', 'top')] },
    },
  ],
}))
const anchorDate = ref<Date | null>(null)

const updateDateRange = (value: string | Date | [string, string] | null) => {
  anchorDate.value = null
  if (value === null) {
    modelValue.value = { startDate: '', endDate: '' }
    return
  }
  if (Array.isArray(value)) {
    modelValue.value = {
      startDate: formatDateOnly(value[0]),
      endDate: formatDateOnly(value[1]),
    }
  }
}

const handleOpen = () => {
  anchorDate.value = null
  datePickerRef.value.open()
}

const onCalendarChange = ([start, end]: [Date, Date | null]) => {
  anchorDate.value = end ? null : start
}

const disabledDate = (date: Date) => {
  if (date.getTime() > Date.now()) {
    return true
  }
  return anchorDate.value != null && !isDateRangeWithinLimit(anchorDate.value, date, props.maxDays)
}
</script>
<template>
  <div class="date-range-selector__container">
    <ButtonComponent v-if="hasCustomButton" variant="transparent" class="custom-button" @click="handleOpen">
      <slot name="customButton" />
    </ButtonComponent>
    <FilterButtonComponent
      v-else
      :class="['date-range-selector__button', { 'date-range-selector__button--borderless': borderless }]"
      :value="label"
      :is-active="false"
      @click="handleOpen"
    >
      <template #label>
        <FlexComponent>
          <TypographyComponent :variant="borderless ? 'label-large' : 'label-medium'">
            {{ label }}
          </TypographyComponent>
          <el-icon :size="borderless ? 18 : 14">
            <ChevronIcon />
          </el-icon>
        </FlexComponent>
      </template>
    </FilterButtonComponent>
    <DatepickerComponent
      ref="datePickerRef"
      :model-value="dateRange"
      size="small"
      :type="showClearButton ? 'datetimerange' : 'daterange'"
      :popper-options="popperOptions"
      popper-class="date-range-selector__popper"
      :shortcuts="shortcuts"
      :disabled-date="disabledDate"
      :show-confirm="false"
      @update:model-value="updateDateRange"
      @calendar-change="onCalendarChange"
    />
  </div>
</template>
<style lang="scss" scoped>
.date-range-selector__container {
  position: relative;
}
.date-range-selector__button {
  border-radius: 8px;
  border: 1px solid rgba(34, 34, 34, 0.12);
  position: relative;
  z-index: 10;
}
.date-range-selector__button.date-range-selector__button--borderless {
  border: none;
  border-radius: 8px;
  padding: 8px;
  background: transparent;
  &:hover {
    background: transparent;
  }
}
:deep(.el-input__wrapper) {
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
}

:global(.date-range-selector__popper .el-date-range-picker__time-header) {
  display: none;
}
:global(.date-range-selector__popper .el-picker-panel__footer) {
  border-radius: 0 0 16px 16px;
  padding: 0;
}
:global(.date-range-selector__popper .el-picker-panel__footer .el-picker-panel__link-btn) {
  line-height: 24px;
  width: 100%;
  padding: 16px;
  font-size: 16px;
  font-weight: 400;
  color: var(--primary);
  border-radius: inherit;
}

.button.custom-button {
  position: relative;
  z-index: 10;
  padding-left: 8px;
  padding-right: 8px;
  text-align: left;
  border-radius: 8px;
  &:hover {
    background: var(--button-hover);
  }
}
</style>
